import {SiteStore} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/site-store/SiteStore';
import {APP_DEFINITION_ID} from '@wix/wixstores-client-core/dist/es/src/constants';
import {StoreMetaDataService} from './StoreMetaDataService';
import {ICanCheckoutResponse, IProductDTO, IPropsInjectedByViewerScript, IStoreMetaData} from '../types/app-types';
import {ModalManager} from '@wix/wixstores-client-core/dist/es/src/modalManager/modalManager';
import {baseModalUrl, ModalState, ModalTheme, ModalType, Origin} from '../constants';
import {SPECS} from '../specs';
import {ViewerContext} from '@wix/wixstores-client-storefront-common';
import _ from 'lodash';
import {ReadOnlyExperiments} from '@wix/wix-experiments';
import {
  notAcceptPaymentsVisitorPopupSf,
  showMerchantPaymentPopupSf,
  showMerchantUpgradePopupSf,
  showShippingPopupSf,
  subscriptionsAreComingSoonVisitorPopupSf,
  viewCheckoutInLiveSitePopupSf,
} from '@wix/bi-logger-ec-sf/v2';
import {
  checkoutNotAbleToAcceptPaymentsVisitorPopupSf,
  checkoutShowMerchantPaymentPopupSf,
} from '@wix/bi-logger-ec-site/v2';

export interface CheckoutInfo {
  checkoutRelativeUrl: string;
  siteBaseUrl: string;
  thankYouPageUrl: string;
  cartUrl: string;
  paymentMethodName: string;
  locale: string;
  deviceType: string;
  a11y: boolean;
  originType: string;
  cashierPaymentId?: string;
  checkoutId: string;
}

export class CheckoutService {
  private readonly siteStore: SiteStore;
  private readonly modalManger: ModalManager;
  private storeMetaData: IStoreMetaData;

  constructor(
    siteStore: SiteStore,
    private readonly viewerContext: ViewerContext,
    private readonly nextProps: (additionalProps: Partial<IPropsInjectedByViewerScript>) => void,
    private readonly isEditorX: boolean,
    private readonly storeMetaDataService: StoreMetaDataService,
    private readonly experiments: ReadOnlyExperiments
  ) {
    this.siteStore = siteStore;
    const openModal = async (url, width, height) => {
      return this.siteStore.windowApis.openModal(url, {width, height, theme: ModalTheme.BARE});
    };
    this.modalManger = new ModalManager({openModal}, baseModalUrl, this.siteStore.instanceManager.getInstance());
  }

  public openModalByType = async (modalType: ModalType): Promise<void> => {
    const biParams = {origin: Origin.PRODUCT_PAGEֹֹ_CHECKOUT, mode: 'editor', isMerchant: true};

    switch (modalType) {
      case ModalType.SetShipping: {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.siteStore.webBiLogger.report(
          showShippingPopupSf({
            type: 'merchant pop-up',
            ...biParams,
          })
        );
        return this.modalManger.openSetShippingMethod();
      }

      case ModalType.SetPayment: {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        void this.siteStore.webBiLogger.report(showMerchantPaymentPopupSf(biParams));
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        void this.siteStore.webBiLogger.report(checkoutShowMerchantPaymentPopupSf(biParams));
        return this.modalManger.openSetPaymentMethod();
      }
      case ModalType.UpgradeToPremium: {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.siteStore.webBiLogger.report(showMerchantUpgradePopupSf(biParams));
        await this.modalManger.openUpgradeToPremium({isEditorX: this.isEditorX});
        break;
      }
      case ModalType.NotInLiveSite: {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.siteStore.webBiLogger.report(viewCheckoutInLiveSitePopupSf(biParams));
        return this.modalManger.openNotInLiveSite();
      }
      case ModalType.Subscriptions: {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        void this.siteStore.webBiLogger.report(
          notAcceptPaymentsVisitorPopupSf({origin: Origin.PRODUCT_PAGEֹֹ_CHECKOUT})
        );
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        void this.siteStore.webBiLogger.report(
          checkoutNotAbleToAcceptPaymentsVisitorPopupSf({
            origin: Origin.PRODUCT_PAGEֹֹ_CHECKOUT,
          })
        );
        this.nextProps({modalState: ModalState.OPEN});
        return this.modalManger.openSubscriptions().then(() => {
          this.nextProps({modalState: ModalState.CLOSE});
        });
      }
      case ModalType.HighArpuSubscriptions: {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.siteStore.webBiLogger.report(
          subscriptionsAreComingSoonVisitorPopupSf({origin: Origin.PRODUCT_PAGEֹֹ_CHECKOUT})
        );
        this.nextProps({modalState: ModalState.OPEN});
        return this.modalManger.openUpgradeSubscriptions({isEditorX: this.isEditorX}).then(() => {
          this.nextProps({modalState: ModalState.CLOSE});
        });
      }
      case ModalType.NoOnlinePayments: {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        void this.siteStore.webBiLogger.report(
          notAcceptPaymentsVisitorPopupSf({origin: Origin.PRODUCT_PAGEֹֹ_CHECKOUT})
        );
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        void this.siteStore.webBiLogger.report(
          checkoutNotAbleToAcceptPaymentsVisitorPopupSf({
            origin: Origin.PRODUCT_PAGEֹֹ_CHECKOUT,
          })
        );
        this.nextProps({modalState: ModalState.OPEN});
        return this.modalManger.openNoOnlinePayments().then(() => {
          this.nextProps({modalState: ModalState.CLOSE});
        });
      }
    }
  };

  private get isCheckoutPagePreviewEnabled(): boolean {
    return this.experiments.enabled(SPECS.CheckoutPagePreviewEnabled);
  }

  /* istanbul ignore next: deprecated - to be removed with UseGetClientConfigFromPublicApi FT */
  private readonly isSubscriptionsPremiumFeature = (): boolean => {
    return (
      _.findIndex(this.viewerContext.premiumFeatures, (feature) => {
        return feature.name === 'stores_subscriptions';
      }) !== -1
    );
  };

  /* istanbul ignore next: todo: test */
  private readonly checkIsCanCheckout = (product: IProductDTO, isSubscribe = false): ICanCheckoutResponse => {
    const canStoreShip = product.productType === 'digital' || this.storeMetaData.storeInfo.canStoreShip;

    const areCheckoutErrorModalsHandledExternally =
      this.experiments.enabled(SPECS.UseCartV2ForDirectPurchase) &&
      this.experiments.enabled(SPECS.DisplayCheckoutErrorModalsForExpressButtons);

    if (areCheckoutErrorModalsHandledExternally) {
      if (
        isSubscribe &&
        !this.experiments.enabled(SPECS.UseGetClientConfigFromPublicApi) &&
        !this.isSubscriptionsPremiumFeature()
      ) {
        return {modalType: ModalType.Subscriptions, canCheckout: false};
      }
      return {modalType: undefined, canCheckout: true};
    }

    if (this.siteStore.isPreviewMode() || this.siteStore.isEditorMode()) {
      return this.getEditorPreviewCanCheckoutResponse(canStoreShip, isSubscribe);
    } else if (
      !this.storeMetaData.storeInfo.isPremium ||
      !this.storeMetaData.storeInfo.hasCreatedPaymentMethods ||
      !canStoreShip
    ) {
      return {modalType: ModalType.NoOnlinePayments, canCheckout: false};
    } else if (
      isSubscribe &&
      !this.experiments.enabled(SPECS.UseGetClientConfigFromPublicApi) &&
      !this.isSubscriptionsPremiumFeature()
    ) {
      return {modalType: ModalType.Subscriptions, canCheckout: false};
    }

    return {modalType: undefined, canCheckout: true};
  };

  private getEditorPreviewCanCheckoutResponse(canStoreShip: boolean, isSubscribe: boolean): ICanCheckoutResponse {
    if (!canStoreShip) {
      return {modalType: ModalType.SetShipping, canCheckout: false};
    } else if (!this.storeMetaData.storeInfo.hasCreatedPaymentMethods) {
      return {modalType: ModalType.SetPayment, canCheckout: false};
    } else if (!this.storeMetaData.storeInfo.isPremium) {
      return {modalType: ModalType.UpgradeToPremium, canCheckout: false};
    } else if (
      isSubscribe &&
      !this.experiments.enabled(SPECS.UseGetClientConfigFromPublicApi) &&
      !this.isSubscriptionsPremiumFeature()
    ) {
      return {modalType: ModalType.HighArpuSubscriptions, canCheckout: false};
    } else if (!this.isCheckoutPagePreviewEnabled) {
      return {modalType: ModalType.NotInLiveSite, canCheckout: false};
    }
    return {modalType: undefined, canCheckout: true};
  }

  /* istanbul ignore next: todo: test */
  public checkIsAllowedToCheckoutSync = (product: IProductDTO, isSubscribe = false): ICanCheckoutResponse => {
    this.storeMetaData = this.storeMetaDataService.storeMetaData;

    /* istanbul ignore next */
    if (!this.storeMetaData) {
      return {modalType: undefined, canCheckout: false};
    }

    const result = this.checkIsCanCheckout(product, isSubscribe);

    const areExpressCheckoutErrorModalsHandledExternally =
      this.experiments.enabled(SPECS.UseCartV2ForDirectPurchase) &&
      this.experiments.enabled(SPECS.DisplayCheckoutErrorModalsForExpressButtons);

    if (
      !areExpressCheckoutErrorModalsHandledExternally &&
      result.canCheckout &&
      this.isCheckoutPagePreviewEnabled &&
      (this.siteStore.isPreviewMode() || this.siteStore.isEditorMode())
    ) {
      return {modalType: ModalType.NotInLiveSite, canCheckout: false};
    }

    return result;
  };

  public checkOldIsAllowedToCheckout = async (
    product: IProductDTO,
    isSubscribe = false
  ): Promise<ICanCheckoutResponse> => {
    this.storeMetaData = await this.storeMetaDataService.fetchStoreInfo().catch((e) => {
      throw e;
    });

    return this.checkIsCanCheckout(product, isSubscribe);
  };

  public navigateToCheckout = async (checkoutInfo: CheckoutInfo): Promise<void> => {
    const ecomPublicApi = await this.siteStore.siteApis.getPublicAPI(APP_DEFINITION_ID);
    const isUseCartV2ForDirectPurchase = this.experiments.enabled(SPECS.UseCartV2ForDirectPurchase);

    if (!isUseCartV2ForDirectPurchase) {
      return ecomPublicApi.navigate.toCheckout({
        checkoutId: checkoutInfo.checkoutId,
        isPreselectedFlow: this.storeMetaData.shipping.isPickupOnly,
      });
    }

    return ecomPublicApi.navigate.toCheckout({checkoutId: checkoutInfo.checkoutId});
  };
}
